import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { LoginCredentials, RegisterData, AuthState } from '../types';
import { authService, sessionHasExpired } from '../services/authService';
import { isDesktop, tokenStorage } from '../services/tokenStorage';

/* T317 — *"pin should/login screen should be brought only when auto logged out or use logout
   manually"*. So the app records WHY it is showing the login screen, and nothing else in the
   codebase is allowed to drop the token. `expired` is the two-day sweep, `manual` is the menu. */
export type LogoutReason = 'manual' | 'expired';
export const LOGOUT_REASON_KEY = 'forge_logout_reason';

function noteLogoutReason(reason: LogoutReason): void {
  try { localStorage.setItem(LOGOUT_REASON_KEY, reason); } catch { /* ignore */ }
}

/** Read the reason for the current login screen, and clear it so it is shown once. */
export function takeLogoutReason(): LogoutReason | null {
  try {
    const value = localStorage.getItem(LOGOUT_REASON_KEY) as LogoutReason | null;
    if (value) localStorage.removeItem(LOGOUT_REASON_KEY);
    return value === 'manual' || value === 'expired' ? value : null;
  } catch {
    return null;
  }
}

interface AuthContextType extends AuthState {
  login: (credentials: LoginCredentials) => Promise<void>;
  loginWithPin: (identifier: string, pin: string) => Promise<void>;
  register: (data: RegisterData) => Promise<void>;
  logout: () => void;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<AuthState>(() => {
    // Check for impersonation handoff from CDPL admin opening Forge in a new tab
    const params = new URLSearchParams(window.location.search);
    if (params.get('impersonate') === '1') {
      const impToken = localStorage.getItem('forge_impersonate_token');
      const impUser = localStorage.getItem('forge_impersonate_user');
      if (impToken && impUser) {
        // Store in sessionStorage (tab-scoped) to avoid clobbering
        // the platform admin's localStorage token in the original tab
        sessionStorage.setItem('token', impToken);
        sessionStorage.setItem('user', impUser);
        // Mirror into the tokenStorage cache so the request interceptor
        // sees the impersonation token immediately on this render.
        void tokenStorage.set('token', impToken, 'session');
        localStorage.removeItem('forge_impersonate_token');
        localStorage.removeItem('forge_impersonate_user');
        // Clean up URL param
        params.delete('impersonate');
        const cleanUrl = params.toString()
          ? `${window.location.pathname}?${params.toString()}`
          : window.location.pathname;
        window.history.replaceState({}, '', cleanUrl);
        const parsed = JSON.parse(impUser);
        return {
          user: parsed,
          token: impToken,
          isAuthenticated: true,
          isLoading: true, // will refresh profile
        };
      }
    }
    // On desktop, the token lives in OS-secure storage and is hydrated
    // asynchronously below — start in a loading state so the user does not
    // briefly see the login screen between mount and hydration.
    if (isDesktop && !tokenStorage.isHydrated()) {
      return {
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: true,
      };
    }
    const token = authService.getToken();
    const storedUser = authService.getStoredUser();
    return {
      user: storedUser,
      token,
      isAuthenticated: !!token && !!storedUser,
      isLoading: !!token,
    };
  });

  // Track whether we just logged in so we can skip aggressive logout on 401
  const justLoggedIn = useRef(false);

  const refreshUser = useCallback(async () => {
    // Get token from localStorage to avoid stale closure
    const token = authService.getToken();
    if (token) {
      try {
        const user = await authService.getProfile();
        setState(prev => ({ ...prev, user, token, isAuthenticated: true, isLoading: false }));
      } catch (error) {
        console.error('Failed to fetch user profile:', error);
        // If we JUST logged in, keep the session alive (token might be
        // malformed/instant-expiry due to backend misconfiguration, but
        // the login itself succeeded — don't kick the user out instantly).
        if (justLoggedIn.current) {
          console.warn('Profile fetch failed after login — keeping session alive');
          setState(prev => ({ ...prev, isLoading: false }));
        } else {
          authService.logout();
          setState({
            user: null,
            token: null,
            isAuthenticated: false,
            isLoading: false
          });
        }
      }
    } else {
      setState(prev => ({ ...prev, isLoading: false }));
    }
  }, []);

  useEffect(() => {
    // On desktop, wait for the secure-store hydration to complete before
    // making any auth decisions. On web, hydrate is a sync snapshot and
    // resolves immediately.
    void (async () => {
      await tokenStorage.hydrate();
      const token = authService.getToken();
      const storedUser = authService.getStoredUser();
      setState(prev => ({
        ...prev,
        user: storedUser ?? prev.user,
        token: token ?? prev.token,
        isAuthenticated: !!token,
        isLoading: !!token,
      }));
      await refreshUser();
    })();
  }, [refreshUser]);

  // Listen for 401 session-expired events from the API interceptor
  useEffect(() => {
    const handleSessionExpired = () => {
      noteLogoutReason('expired');
      authService.logout();
      setState({ user: null, token: null, isAuthenticated: false, isLoading: false });
    };
    window.addEventListener('auth:session-expired', handleSessionExpired);
    return () => window.removeEventListener('auth:session-expired', handleSessionExpired);
  }, []);

  /* T316 — the two-day sweep. The SERVER is the authority: its session row expires and the
     refresh fails, which lands on the listener above. This is the case where the server never
     gets asked — a window left open over a weekend, or an app opened with no network. It checks
     on a timer AND on focus, because a sleeping machine does not fire intervals. */
  useEffect(() => {
    const sweep = () => {
      if (!authService.getToken()) return;
      if (!sessionHasExpired()) return;
      noteLogoutReason('expired');
      authService.logout();
      setState({ user: null, token: null, isAuthenticated: false, isLoading: false });
    };
    sweep();
    const timer = window.setInterval(sweep, 60_000);
    window.addEventListener('focus', sweep);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener('focus', sweep);
    };
  }, []);

  const login = async (credentials: LoginCredentials) => {
    const { user, token } = await authService.login(credentials);
    justLoggedIn.current = true;
    setState({
      user,
      token,
      isAuthenticated: true,
      isLoading: false
    });
    // Clear the flag after a grace period
    setTimeout(() => { justLoggedIn.current = false; }, 15_000);
  };

  /** T314 — the PIN door into the same session. */
  const loginWithPin = async (identifier: string, pin: string) => {
    const { user, token } = await authService.loginWithPin(identifier, pin);
    justLoggedIn.current = true;
    setState({ user, token, isAuthenticated: true, isLoading: false });
    setTimeout(() => { justLoggedIn.current = false; }, 15_000);
  };

  const register = async (data: RegisterData) => {
    const { user, token } = await authService.register(data);
    setState({
      user,
      token,
      isAuthenticated: true,
      isLoading: false
    });
  };

  const logout = () => {
    noteLogoutReason('manual');
    authService.logout();
    setState({
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false
    });
  };

  return (
    <AuthContext.Provider value={{ ...state, login, loginWithPin, register, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

